import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useMutation, useQueries, useQuery, useQueryClient} from '@tanstack/react-query';
import AIChatPanel from '@/components/wyscout/AIChatPanel';
import {SkeletonBlock} from '@/components/ui/LoadingSkeleton';
import OperationStatus from '@/components/ui/OperationStatus';
import {PlayerDetail, playersV2API} from '@/features/players/api';
import {PlayerAIProfile, PlayerAIProfileSample, PlayerAIProfileScope, playerAIProfileAPI, playerAIProfileKey} from '@/features/players/playerAIProfileApi';
import {WyscoutAnalyticsDashboard, WyscoutAnalyticsFilters} from '@/features/players/wyscoutAnalyticsApi';
import {AIChatMessage, wyscoutMatchesAPI} from '@/services/api';
import AIProfilePeerSelect, {PeerIdentity} from './AIProfilePeerSelect';
import AIProfileReport from './AIProfileReport';

interface Props {
  workspaceId: string;
  playerId: string;
  playerName: string;
  filters: WyscoutAnalyticsFilters;
  filterOptions: WyscoutAnalyticsDashboard['filterOptions'];
  peerIds: string[];
  onPeerIdsChange: (peerIds: string[]) => void;
  onChangeDashboard?: () => void;
}

const scopeLabels = (filters: WyscoutAnalyticsFilters, options: WyscoutAnalyticsDashboard['filterOptions']): string[] => {
  const season = filters.seasonId === null ? 'All seasons' : `Season ${options.seasons.find(item => item.id === filters.seasonId)?.label ?? filters.seasonId}`;
  const matches = filters.matchIds.length === 0 ? 'All matches' : `${filters.matchIds.length} selected ${filters.matchIds.length === 1 ? 'match' : 'matches'}`;
  return [`${filters.lens[0].toUpperCase()}${filters.lens.slice(1)} lens`, season, matches];
};

interface GenerationVariables {
  workspaceId: string;
  playerId: string;
  scope: PlayerAIProfileScope;
  queryKey: ReturnType<typeof playerAIProfileKey>;
}

const AIProfileInlineFollowUp: React.FC<{playerId: string; profile: PlayerAIProfile}> = ({playerId, profile}) => {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<AIChatMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const content = input.trim();
    if (!content || loading) return;
    const nextMessages: AIChatMessage[] = [...messages, {role: 'user', content}];
    setMessages(nextMessages);
    setInput('');
    setError(null);
    setLoading(true);
    try {
      const response = await wyscoutMatchesAPI.sendPlayerChat(playerId, nextMessages, undefined, sessionId, profile.id);
      setMessages([...nextMessages, {role: 'assistant' as const, content: response.data.reply}].slice(-100));
      setSessionId(response.data.sessionId || sessionId);
    } catch (err: any) {
      const payload = err?.response?.data?.error;
      setError(typeof payload === 'string' ? payload : payload?.message || err?.message || 'Failed to get AI response');
    } finally {
      setLoading(false);
    }
  };

  return <section aria-labelledby="ai-profile-follow-up-heading" className="rounded-lg border border-club-blue-200 bg-club-blue-50/70 p-4 dark:border-club-blue-900/70 dark:bg-club-blue-950/20">
    <div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h3 id="ai-profile-follow-up-heading" className="font-semibold text-gray-900 dark:text-gray-100">Ask a follow-up</h3>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Ask another question grounded in this saved AI profile.</p>
      </div>
    </div>
    <form onSubmit={submit} className="mt-3 flex min-w-0 flex-col gap-2 sm:flex-row">
      <label className="min-w-0 flex-1 text-sm font-semibold text-gray-800 dark:text-gray-100">
        Ask a follow-up about this AI profile
        <input
          type="text"
          value={input}
          onChange={event => setInput(event.target.value)}
          disabled={loading}
          placeholder="Ask how to apply this profile in coaching..."
          className="mt-1 min-h-11 w-full rounded-md border border-gray-300 bg-white px-3 text-gray-900 placeholder-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:placeholder-gray-400"
        />
      </label>
      <button type="submit" disabled={loading || !input.trim()} className="min-h-11 rounded-md bg-club-blue-600 px-4 font-semibold text-white hover:bg-club-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 disabled:cursor-not-allowed disabled:opacity-60 sm:self-end">{loading ? 'Asking...' : 'Ask follow-up'}</button>
    </form>
    {messages.length > 0 && <div className="mt-3 space-y-2" aria-live="polite">
      {messages.slice(-4).map((message, index) => <div key={`${message.role}:${index}:${message.content}`} className={message.role === 'user' ? 'rounded-md bg-white p-3 text-sm text-gray-800 dark:bg-gray-900 dark:text-gray-100' : 'rounded-md bg-gray-900 p-3 text-sm text-white dark:bg-gray-950'}>
        <p className="text-xs font-semibold uppercase tracking-wide opacity-70">{message.role === 'user' ? 'You asked' : 'AI answer'}</p>
        <p className="mt-1 break-words">{message.content}</p>
      </div>)}
    </div>}
    {error && <p role="alert" className="mt-3 rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/30 dark:text-red-300">{error}</p>}
  </section>;
};

const AIProfileContent: React.FC<Props & {knownPeers: Record<string, PeerIdentity>; setKnownPeers: React.Dispatch<React.SetStateAction<Record<string, PeerIdentity>>>}> = ({workspaceId, playerId, playerName, filters, filterOptions, peerIds, onPeerIdsChange, onChangeDashboard, knownPeers, setKnownPeers}) => {
  const queryClient = useQueryClient();
  const [chatOpen, setChatOpen] = useState(false);
  const submitting = useRef(false);
  const scope: PlayerAIProfileScope = {
    lens: filters.lens,
    seasonId: filters.seasonId,
    matchIds: [...filters.matchIds].sort((a, b) => a - b),
    peerIds: [...peerIds],
  };
  const queryKey = playerAIProfileKey(workspaceId, playerId, scope);
  const query = useQuery<PlayerAIProfile | null>({
    queryKey,
    queryFn: ({signal}) => playerAIProfileAPI.latest(workspaceId, playerId, scope, signal),
    retry: false,
  });
  const generation = useMutation({
    mutationFn: (submitted: GenerationVariables) => playerAIProfileAPI.generate(
      submitted.workspaceId, submitted.playerId, submitted.scope, new AbortController().signal,
    ),
    onSuccess: (generated, submitted) => queryClient.setQueryData(submitted.queryKey, generated),
    onSettled: () => { submitting.current = false; },
  });
  const generate = () => {
    if (submitting.current) return;
    submitting.current = true;
    const submittedScope = {...scope, matchIds: [...scope.matchIds], peerIds: [...scope.peerIds]};
    generation.mutate({
      workspaceId,
      playerId,
      scope: submittedScope,
      queryKey: playerAIProfileKey(workspaceId, playerId, submittedScope),
    });
  };
  const labels = scopeLabels(filters, filterOptions);
  const profile: PlayerAIProfile | null | undefined = query.data;
  const sampleNames = new Map<string, PlayerAIProfileSample>(profile?.evidence.samples.slice(1).map((sample: PlayerAIProfileSample) => [sample.playerId, sample]) ?? []);
  const peerDetails = useQueries({queries: peerIds.map(id => ({
    queryKey: ['ai-profile-peer-detail', workspaceId, id],
    queryFn: () => playersV2API.detail<PlayerDetail>(id, workspaceId),
    enabled: !query.isPending && !sampleNames.has(id) && !knownPeers[id],
    retry: false,
  }))});
  const peers: PeerIdentity[] = peerIds.map(id => {
    const sample = sampleNames.get(id);
    const detail = peerDetails[peerIds.indexOf(id)]?.data?.data as PlayerDetail | undefined;
    return sample
      ? {id, name: sample.name, position: sample.position}
      : knownPeers[id] ?? (detail ? {id, name: detail.name, position: detail.position} : {id, name: 'Selected peer', position: 'Loading'});
  });
  const listProfileSessions = useCallback(async () => {
    if (!profile) return [];
    const response = await wyscoutMatchesAPI.listPlayerChatSessions(playerId);
    return response.data.filter(session => session.profileGrounded && session.profileId === profile.id);
  }, [playerId, profile]);

  return <div className="min-w-0 space-y-5">
    <section aria-labelledby="ai-profile-scope-heading" className="space-y-3">
      <div>
        <h2 id="ai-profile-scope-heading" className="text-xl font-bold text-gray-900 dark:text-gray-100">AI profile</h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Generate a saved report for {playerName} grounded in this analytics selection.</p>
      </div>
      <ul aria-label="AI profile evidence scope" className="flex flex-wrap gap-2">{labels.map(label => <li key={label} className="rounded-full bg-gray-100 px-3 py-1 text-sm font-semibold text-gray-700 dark:bg-gray-800 dark:text-gray-200">{label}</li>)}</ul>
      {filters.matchIds.length > 0 && <p className="break-words text-sm text-gray-600 dark:text-gray-300">Matches: {filters.matchIds.map(id => filterOptions.matches.find(match => match.id === id)?.opponent ?? id).join(', ')}</p>}
      {onChangeDashboard && <button type="button" onClick={onChangeDashboard} className="min-h-11 rounded-md border border-gray-300 px-3 font-semibold text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 dark:border-gray-700 dark:text-gray-200">Change in Dashboard</button>}
      <AIProfilePeerSelect workspaceId={workspaceId} playerId={playerId} selected={peers} onChange={next => {
        generation.reset();
        setKnownPeers(Object.fromEntries(next.map(peer => [peer.id, peer])));
        onPeerIdsChange(next.map(peer => peer.id));
      }} />
    </section>

    {query.isPending && <div role="status" aria-label="Loading saved AI profile" aria-live="polite"><SkeletonBlock className="h-28 overflow-hidden" /></div>}
    {query.isError && <div className="rounded-lg bg-red-50 p-4 dark:bg-red-950/30"><p role="alert" className="text-sm text-red-700 dark:text-red-300">Unable to load the saved AI profile.</p><button type="button" onClick={() => { void query.refetch(); }} className="mt-3 min-h-11 rounded-md border border-red-300 px-3 font-semibold text-red-700 dark:border-red-800 dark:text-red-300">Retry profile</button></div>}

    {!query.isPending && !query.isError && !profile && <section className="rounded-lg bg-gray-50 p-5 text-center ring-1 ring-gray-200 dark:bg-gray-950 dark:ring-gray-800">
      <p className="text-gray-700 dark:text-gray-200">No saved profile matches this evidence scope.</p>
      <button type="button" disabled={generation.isPending} onClick={generate} className="mt-4 min-h-11 rounded-md bg-club-blue-600 px-4 font-semibold text-white hover:bg-club-blue-700 disabled:cursor-not-allowed disabled:opacity-60">Generate AI profile</button>
    </section>}

    {generation.isPending && <OperationStatus state="pending" label="Generating AI profile" />}
    {generation.isError && <p role="alert" className="rounded-lg bg-red-50 p-4 text-sm text-red-700 dark:bg-red-950/30 dark:text-red-300">{profile ? 'Unable to generate the AI profile. The previous profile is still shown.' : 'Unable to generate the AI profile. Please try again.'}</p>}

    {profile && <AIProfileReport profile={profile} regenerating={generation.isPending} onRegenerate={generate} onAskFollowUp={() => setChatOpen(true)} followUp={<AIProfileInlineFollowUp playerId={playerId} profile={profile} />} />}
    {profile && <AIChatPanel
      key={profile.id}
      open={chatOpen}
      onOpenChange={setChatOpen}
      title={`Ask about ${playerName}`}
      entityType="player"
      entityId={playerId}
      listSessions={listProfileSessions}
      presets={[]}
      onSendMessage={async (messages, preset, sessionId) => (await wyscoutMatchesAPI.sendPlayerChat(playerId, messages, preset, sessionId, profile.id)).data}
    />}
  </div>;
};

const AIProfile: React.FC<Props> = props => {
  const [knownPeers, setKnownPeers] = useState<Record<string, PeerIdentity>>({});
  useEffect(() => setKnownPeers({}), [props.workspaceId, props.playerId]);
  return <AIProfileContent
    key={`${props.workspaceId}:${props.playerId}:${props.filters.lens}:${props.filters.seasonId ?? 'all'}:${[...props.filters.matchIds].sort((a, b) => a - b).join(',')}:${props.peerIds.join(',')}`}
    {...props}
    knownPeers={knownPeers}
    setKnownPeers={setKnownPeers}
  />;
};

export default AIProfile;
